import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Video, TrendingUp, Award, X, Clock, AlertCircle, CreditCard } from 'lucide-react';
import api from '../api/axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

export const Dashboard = () => {
  const { user, refreshUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, bookingsRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/bookings')
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (booking) => {
    setCancellingId(booking.id);
    try {
      const response = await api.post(`/bookings/${booking.id}/cancel`);
      
      if (response.data.credit_refunded) {
        toast.success('Turno cancelado. Se te devolvió 1 crédito.');
      } else {
        toast.warning('Turno cancelado sin devolución de crédito (menos de 1 hora de anticipación).');
      }
      
      // Actualizar datos
      await fetchData();
      await refreshUser();
      setShowCancelModal(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar el turno');
    } finally {
      setCancellingId(null);
    }
  };

  const upcomingBookings = bookings.filter(b => 
    b.status !== 'cancelled' && b.hours_until > 0
  ).sort((a, b) => new Date(a.date) - new Date(b.date));

  const formatTimeUntil = (hours) => {
    if (hours < 1) {
      return `${Math.round(hours * 60)} minutos`;
    } else if (hours < 24) {
      return `${Math.round(hours)} horas`;
    } else {
      return `${Math.round(hours / 24)} días`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="dashboard-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-20 px-6 md:px-12 lg:px-24" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-bold uppercase tracking-tight mb-2" data-testid="dashboard-title">Dashboard</h1>
          <p className="text-muted-foreground" data-testid="dashboard-subtitle">Bienvenido a City Virtual Golf</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="glass-effect border-white/10" data-testid="stat-credits">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Créditos Disponibles</CardTitle>
              <CreditCard className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary" data-testid="stat-credits-count">{user?.credits || 0}</div>
              <p className="text-xs text-muted-foreground">Para reservar turnos</p>
            </CardContent>
          </Card>

          <Card className="glass-effect border-white/10" data-testid="stat-analyses">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Análisis Totales</CardTitle>
              <Video className="h-4 w-4 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" data-testid="stat-analyses-count">{stats?.total_analyses || 0}</div>
              <p className="text-xs text-muted-foreground">Videos analizados</p>
            </CardContent>
          </Card>

          <Card className="glass-effect border-white/10" data-testid="stat-bookings">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Turnos Reservados</CardTitle>
              <Calendar className="h-4 w-4 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" data-testid="stat-bookings-count">{stats?.total_bookings || 0}</div>
              <p className="text-xs text-muted-foreground">Sesiones programadas</p>
            </CardContent>
          </Card>

          <Card className="glass-effect border-white/10" data-testid="stat-level">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Plan Actual</CardTitle>
              <Award className="h-4 w-4 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold capitalize" data-testid="stat-level-value">
                {user?.plan === 'none' ? 'Sin Plan' : user?.plan || 'Sin Plan'}
              </div>
              <p className="text-xs text-muted-foreground">
                <Link to="/plans" className="text-primary hover:underline">Ver planes</Link>
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="glass-effect border-white/10" data-testid="upcoming-bookings-card">
            <CardHeader>
              <CardTitle className="font-heading uppercase">Próximos Turnos</CardTitle>
              <CardDescription>Tus próximas sesiones de entrenamiento</CardDescription>
            </CardHeader>
            <CardContent>
              {upcomingBookings.length > 0 ? (
                <div className="space-y-4">
                  {upcomingBookings.map((booking) => (
                    <div 
                      key={booking.id} 
                      className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10" 
                      data-testid={`upcoming-booking-${booking.id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold">{new Date(booking.date).toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' })}</p>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            booking.status === 'confirmed' ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'
                          }`}>
                            {booking.status === 'confirmed' ? 'Confirmado' : 'Pendiente'}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {booking.time_slot} - en {formatTimeUntil(booking.hours_until)}
                        </p>
                      </div>
                      
                      {booking.can_cancel && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowCancelModal(booking)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          data-testid={`cancel-booking-${booking.id}`}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Cancelar
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8" data-testid="no-bookings">
                  <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">No tenés turnos próximos</p>
                  <Link to="/booking" className="text-accent hover:underline" data-testid="book-now-link">Reservar ahora</Link>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="glass-effect border-white/10" data-testid="recent-analyses-card">
            <CardHeader>
              <CardTitle className="font-heading uppercase">Últimos Análisis</CardTitle>
              <CardDescription>Tus últimos análisis de swing</CardDescription>
            </CardHeader>
            <CardContent>
              {stats?.recent_analyses?.length > 0 ? (
                <div className="space-y-4">
                  {stats.recent_analyses.map((analysis) => (
                    <Link 
                      key={analysis.id} 
                      to={`/analysis/${analysis.id}`} 
                      className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10 hover:border-primary/50 transition-colors"
                      data-testid={`recent-analysis-${analysis.id}`}
                    >
                      <div>
                        <p className="font-semibold">{analysis.video_filename}</p>
                        <p className="text-sm text-muted-foreground">
                          {analysis.status === 'completed' ? 'Completado' : 'Procesando...'}
                        </p>
                      </div>
                      <Video className="h-5 w-5 text-accent" />
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8" data-testid="no-analyses">
                  <Video className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">No tenés análisis aún</p>
                  <Link to="/analysis" className="text-accent hover:underline" data-testid="analyze-now-link">Analizar ahora</Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Política de cancelación */}
        <Card className="glass-effect border-white/10 mt-6">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Política de Cancelación</p>
                <p>Podés cancelar tus turnos hasta <strong className="text-primary">1 hora antes</strong> del horario reservado y se te devolverá el crédito automáticamente. Las cancelaciones con menos de 1 hora de anticipación no tienen devolución de crédito.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modal de confirmación de cancelación */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowCancelModal(null)}>
          <div className="bg-background border border-white/10 rounded-lg w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">Cancelar Turno</h3>
            
            <div className="p-4 bg-white/5 rounded-lg mb-4">
              <p className="font-semibold">{new Date(showCancelModal.date).toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
              <p className="text-muted-foreground">{showCancelModal.time_slot}</p>
            </div>
            
            {showCancelModal.can_refund ? (
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg mb-4">
                <p className="text-green-400 flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  <span>Se te devolverá <strong>1 crédito</strong> al cancelar</span>
                </p>
              </div>
            ) : (
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg mb-4">
                <p className="text-yellow-400 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  <span><strong>No</strong> se devolverá el crédito (menos de 1 hora)</span>
                </p>
              </div>
            )}
            
            <p className="text-sm text-muted-foreground mb-6">
              ¿Estás seguro de que querés cancelar este turno?
            </p>
            
            <div className="flex gap-3">
              <Button 
                variant="ghost" 
                onClick={() => setShowCancelModal(null)}
                className="flex-1"
              >
                Volver
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => handleCancelBooking(showCancelModal)}
                disabled={cancellingId === showCancelModal.id}
                className="flex-1"
                data-testid="confirm-cancel-button"
              >
                {cancellingId === showCancelModal.id ? 'Cancelando...' : 'Confirmar Cancelación'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
